import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { User } from "../../models/Person";
import { UserAvatar } from "../UserAvatar/UserAvatar";
import { routes } from "../../routes/routes";

import "./SimilarMentorCard.css";

function bioSnippet(fullBio: string | undefined, maxLen = 220): string {
  if (!fullBio?.trim()) return "";
  const text = fullBio.replace(/\s+/g, " ").trim();
  const sentenceMatch = text.match(/^[\s\S]{1,320}?[.!?](?=\s|$)/);
  const lead = (sentenceMatch ? sentenceMatch[0] : text).trim();
  if (lead.length <= maxLen) return lead;
  return `${lead.slice(0, maxLen).trim()}…`;
}

function getLanguageLabel(lang: string | undefined, t: (key: string) => string): string | null {
  if (!lang) return null;
  if (lang === "en") return t("mentor_profile.language_english").toUpperCase();
  if (lang === "es") return t("mentor_profile.language_spanish").toUpperCase();
  if (lang === "both") return `${t("mentor_profile.language_english")} / ${t("mentor_profile.language_spanish")}`.toUpperCase();
  return null;
}

type Props = {
  mentor: User;
};

export function SimilarMentorCard({ mentor }: Props) {
  const { t } = useTranslation();
  const displayName = [mentor.name, mentor.last_name].filter(Boolean).join(" ").trim();
  const firstName = mentor.name?.trim() || displayName;
  const aboutText = bioSnippet(mentor.fullBio);
  const languageLabel = getLanguageLabel(mentor.language, t);

  return (
    <article className="similar-mentor-card">
      <div className="similar-mentor-card__photo">
        <div className="similar-mentor-card__photo-inner">
          <UserAvatar
            avatarUrl={mentor.avatar_url}
            userId={mentor.id}
            firstName={mentor.name}
            lastName={mentor.last_name}
            shape="square"
            alt={displayName}
            lazy
          />
        </div>
      </div>

      <div className="similar-mentor-card__body">
        <h3 className="similar-mentor-card__name">{displayName}</h3>

        {!!mentor.shortDescription && (
          <p className="similar-mentor-card__title">{mentor.shortDescription}</p>
        )}

        {languageLabel && (
          <div className="similar-mentor-card__languages">
            <p className="similar-mentor-card__languages-label">
              {t("mentor_profile.languages_label")}
            </p>
            <span className="similar-mentor-card__language-tag">{languageLabel}</span>
          </div>
        )}

        {aboutText && (
          <>
            <p className="similar-mentor-card__about-label">
              {t("mentor_profile.similar_about_label", { name: firstName.toUpperCase() })}
            </p>
            <p className="similar-mentor-card__about-text">{aboutText}</p>
          </>
        )}

        <div className="similar-mentor-card__actions">
          <NavLink
            className="similar-mentor-card__learn-more"
            to={routes.MENTOR.replace(":id", mentor.id.toString())}
          >
            {t("mentor_profile.learn_more_button")}
          </NavLink>
        </div>
      </div>
    </article>
  );
}
